// Map preview for the create-route wizard. Uses the same Google Maps stack as
// RouteMap.tsx but works against the in-flight selected DN list (no
// persisted stops yet) — pin coordinates come from each DN's
// receiverBranch.locationLat/Lng. Stops without coordinates are listed in
// a small footer pill so the user knows why the polyline doesn't cover
// every selected DN.

import { Anchor, Badge, Box, Group, Paper, Stack, Text } from "@mantine/core"
import { WarehouseIcon } from "@phosphor-icons/react"
import { PolylineF } from "@react-google-maps/api"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import classes from "./wizard.module.css"
import { Link } from "react-router-dom"
import type { DnRow } from "./types"
import { renderBusinessName } from "../../../utils/render-business-name"
import { dashedLine, HtmlMarker, LatLng, MapCanvas, MapPopup, UB_CENTER, useMapColor } from "../../ui/google-map"
import mapClasses from "../../ui/google-map/google-map.module.css"

interface LocatedStop {
  id: string
  refCode: string
  customerName: string
  lat: number
  lng: number
  stopNumber: number
}

interface Props {
  stops          : DnRow[]              // ordered, all selected DNs
  totalDistanceKm: number
  startPin?      : { lat: number; lng: number; name: string } | null
  height?        : number
}

export function WizardMap({ stops, totalDistanceKm, startPin, height = 320 }: Props) {
  const { t } = useTranslation(["delivery"])
  const lineColor = useMapColor("brand")
  // "start" or a stop id; one popup at a time.
  const [openId, setOpenId] = useState<string | null>(null)
  const located = useMemo<LocatedStop[]>(() => {
    return stops
      .map((dn, idx) => {
        const lat = dn.receiverBranch?.locationLat
        const lng = dn.receiverBranch?.locationLng
        if (typeof lat !== "number" || typeof lng !== "number") return null
        return {
          id          : dn.id ?? "",
          refCode     : dn.refCode ?? (dn.id ?? "").slice(0, 8),
          customerName: renderBusinessName(dn.receiverBusiness, "") ||
                        renderBusinessName(dn.receiverBusiness?.partner, "-"),
          lat,
          lng,
          stopNumber  : idx + 1,
        }
      })
      .filter((x): x is LocatedStop => x !== null)
  }, [stops])

  const missingCount = stops.length - located.length
  // Polyline includes the warehouse start pin as the first vertex when set,
  // so the user sees the route leave the depot.
  const polylinePoints = useMemo(() => {
    const pts: LatLng[] = []
    if (startPin) pts.push({ lat: startPin.lat, lng: startPin.lng })
    for (const s of located) pts.push({ lat: s.lat, lng: s.lng })
    return pts
  }, [located, startPin])
  const center: LatLng = polylinePoints[0] ?? UB_CENTER
  const openStop = located.find((s) => s.id === openId)

  return (
    <Box component="section" className={classes.card} style={{ overflow: "hidden" }}>
      <Box className={classes.head}>
        <Text className={classes.title}>{t("delivery:route.wizardUi.map.title")}</Text>
      </Box>

      {stops.length === 0 ? (
        <Box p="lg">
          <Text size="sm" c="dimmed" ta="center">
            {t("delivery:route.wizardUi.map.empty")}
          </Text>
        </Box>
      ) : located.length === 0 ? (
        <Box p="lg">
          <Stack gap={4}>
            <Text size="sm" c="dimmed" ta="center">
              {t("delivery:route.wizardUi.map.noCoords")}
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              {t("delivery:route.wizardUi.map.noCoordsHint")}
            </Text>
          </Stack>
        </Box>
      ) : (
        <Box style={{ position: "relative", height }}>
          <MapCanvas height="100%" center={center} zoom={12} fitPoints={polylinePoints}>
            {polylinePoints.length >= 2 && (
              <PolylineF path={polylinePoints} options={dashedLine(lineColor, "10px")} />
            )}
            {startPin && (
              <HtmlMarker position={startPin} onClick={() => setOpenId("start")} zIndex={1}>
                <Box className={mapClasses.warehouse}>
                  <WarehouseIcon size={16} weight="fill" />
                </Box>
              </HtmlMarker>
            )}
            {located.map((s) => (
              <HtmlMarker key={s.id} position={s} onClick={() => setOpenId(s.id)} zIndex={2}>
                <Box className={mapClasses.pin} data-tone="brand">
                  {s.stopNumber}
                </Box>
              </HtmlMarker>
            ))}
            {openId === "start" && startPin && (
              <MapPopup position={startPin} onClose={() => setOpenId(null)}>
                <Text fz="sm" fw={700}>
                  {t("delivery:route.wizardUi.map.startWarehouse")}
                </Text>
                <Text fz="sm">{startPin.name}</Text>
              </MapPopup>
            )}
            {openStop && (
              <MapPopup position={openStop} offset={14} onClose={() => setOpenId(null)}>
                <Text fz="sm">
                  <Text span fw={700}>
                    #{openStop.stopNumber}
                  </Text>{" "}
                  {openStop.customerName}
                </Text>
                <Anchor component={Link} to={`/supplier-delivery/detail/${openStop.id}`} size="sm">
                  {t("delivery:route.map.openDelivery")}
                </Anchor>
              </MapPopup>
            )}
          </MapCanvas>

          {/* Bottom overlay */}
          <Paper
            radius="md"
            shadow="sm"
            p="xs"
            style={{
              position : "absolute",
              bottom   : 12,
              left     : 12,
              right    : 12,
              zIndex   : 500,
              background: "rgba(255,255,255,0.95)",
            }}
          >
            <Group justify="space-between">
              <Box>
                <Text size="sm" c="gray.7">{t("delivery:route.wizardUi.map.total")}</Text>
                <Text size="sm" fw={700}>{totalDistanceKm > 0 ? `${totalDistanceKm.toFixed(1)} ${t("delivery:route.unit.km")}` : "-"}</Text>
              </Box>
              <Box>
                <Text size="sm" c="gray.7">{t("delivery:route.wizardUi.map.stops")}</Text>
                <Text size="sm" fw={700}>{located.length}</Text>
              </Box>
              {missingCount > 0 && (
                <Box>
                  <Text size="sm" c="gray.7">{t("delivery:route.wizardUi.map.missing")}</Text>
                  <Badge size="sm" color="orange" variant="light" radius="sm">
                    {missingCount}
                  </Badge>
                </Box>
              )}
            </Group>
          </Paper>
        </Box>
      )}
    </Box>
  )
}

// Haversine distance between two lat/lng points (km).
const EARTH_KM = 6371

export function haversineKm(a: [number, number], b: [number, number]): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat  = toRad(b[0] - a[0])
  const dLng  = toRad(b[1] - a[1])
  const lat1  = toRad(a[0])
  const lat2  = toRad(b[0])
  const h     = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.sqrt(h))
}

export function totalRouteKm(
  stops: DnRow[],
  startPin?: { lat: number; lng: number } | null,
): number {
  const pts: [number, number][] = []
  if (startPin) pts.push([startPin.lat, startPin.lng])
  for (const dn of stops) {
    const lat = dn.receiverBranch?.locationLat
    const lng = dn.receiverBranch?.locationLng
    if (typeof lat === "number" && typeof lng === "number") pts.push([lat, lng])
  }
  if (pts.length < 2) return 0
  let total = 0
  for (let i = 1; i < pts.length; i++) {
    total += haversineKm(pts[i - 1], pts[i])
  }
  return total
}
